import React, { useEffect, useState } from "react";
import { ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import ReportGoodNewsCarousel from "../components/ReportGoodNewsCarousel";
import ReportCharacterRow from "../components/ReportCharacterRow";
import ReportBackground from "../components/ReportBackground";
import ReportModalSheet from "../components/ReportModalSheet";
import ReportIndicatorItem from "../components/ReportIndicatorItem";
import ReportEmptyState from "../components/ReportEmptyState";
import { getReportStatistic } from "../services/reportStatistics";
import { formatPeriod } from "../Utils/formatPeriod";
import { formatCharacterName } from "../Utils/formatReportText";
import {
  CalendarTodayIcon,
  ChevronDown,
  DangerIcon,
  Heart2Icon,
  PersonSearchIcon,
  FactCheckIcon,
  ExtensionIcon,
  ThumbUpIcon,
  ProfileIcon,
  PaperOnlyIcon,
} from "../assets/icons";

export default function ReportDashboard() {
  const navigation = useNavigation();
  const route = useRoute();
  const reportId = route.params?.id;
  const [statistic, setStatistic] = useState(null);
  const [period, setPeriod] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [charactersOpen, setCharactersOpen] = useState(false);
  const [childPickerOpen, setChildPickerOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState("");
 const [selectedProfile, setSelectedProfile] = useState(""); 
  const [indicatorType, setIndicatorType] = useState("positive");
  useEffect(() => {
    if (!reportId) {
      setLoadError(true);
      setLoading(false);
      return undefined;
    }

    let active = true;
    getReportStatistic(reportId)
      .then(({ data }) => {
        if (!active) return;
        setStatistic(data);
        setSelectedChild(data?.siswa || "");
        setSelectedProfile(data?.chosenProfile || "");
        setPeriod(data?.periode?.[0] || "");
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reportId]);

  const periods = statistic?.periode || [];
  const summary = statistic?.summaries?.find((item) => item.periode === period) || statistic?.summaries?.[0];
  const allCharacters = summary?.characters || [];
  const charactersWithData = allCharacters.filter((character) =>
    Number(character.total_indicators) > 0 || character.indicators?.length > 0
  );
  const characterData = [...charactersWithData].sort((a, b) => b.point - a.point).slice(0, 3);
  const allIndicators = allCharacters.flatMap((character) =>
    (character.indicators || []).map((indicator) => ({
      ...indicator,
      characterName: character.name,
    }))
  );
  const indicatorData = {
    positive: [...allIndicators].sort((a, b) => b.value - a.value).slice(0, 3),
    attention: [...allIndicators].sort((a, b) => a.value - b.value).slice(0, 3),
  };
  const characterThreshold = summary?.threshold_characters || {};
  const indicatorThreshold = summary?.threshold_indicators || {};
  const passedCount = (characterThreshold.passed || 0) + (indicatorThreshold.passed || 0);
  const failedCount = (characterThreshold.failed || 0) + (indicatorThreshold.failed || 0);
  const hasThresholdData = passedCount + failedCount > 0;
  const progress = hasThresholdData
    ? Math.round((passedCount / (passedCount + failedCount)) * 100)
    : 0;
  const openCharacter = (character) => {
    if (!character) return;
    navigation.navigate("ReportIndicatorDetail", { character, summary, statistic, period, id: reportId });
  };
  const openAllIndicators = () => navigation.navigate("ReportIndicatorDetail", {
    summary,
    statistic,
    period,
    id: reportId,
    showAllIndicators: true,
  });

  if (loading) {
    return (
      <ReportBackground style={styles.loadingScreen}>
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color="#6724E2" />
          <Text style={styles.loadingText}>Memuat laporan...</Text>
        </View>
      </ReportBackground>
    );
  }

  return (
    <ReportBackground>
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <View style={styles.headerActions}>
        <View style={styles.avatar}>
          <Pressable accessibilityRole="button" accessibilityLabel="Pilih data anak" onPress={() => setChildPickerOpen(true)}>
            <PersonSearchIcon width={24} height={24} />
          </Pressable>
        </View>
        <Pressable
          style={styles.inputData}
          onPress={() => navigation.navigate("ReportInputData", { statistic, summary, period, id: reportId })}
        >
          <PaperOnlyIcon width={18} height={18} />
          <Text style={styles.inputDataText}>Input Data</Text>
        </Pressable>
      </View>

      <View style={styles.greeting}>
        <Text style={styles.greetingTitle}>Halo Ayah Bunda</Text>
        <Text style={styles.greetingSubtitle}>Berikut Laporan Ananda</Text>
      </View>

      {loadError && <Text style={styles.apiMessage}>Laporan belum dapat dimuat.</Text>}

      <View style={styles.schoolPeriodCard}>
        <View style={styles.schoolInfo}>
          <Image source={require("../assets/dummy-logo.svg")} style={styles.schoolLogo} resizeMode="contain" />
          <View style={styles.schoolDivider} />
          <Text style={styles.schoolName}>SMK Telkom Banjarbaru</Text>
        </View>
        <View style={styles.schoolPeriodDivider} />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Periode ${formatPeriod(period) || "belum tersedia"}`}
          onPress={() => setPeriodOpen(true)}
          disabled={periods.length === 0}
          style={styles.periodButton}
        >
          <Text style={styles.periodLabel}>Periode</Text>
          <Text style={[styles.periodValue, !period && styles.periodUnavailable]}>
            {formatPeriod(period) || "Belum tersedia"}
          </Text>
          <ChevronDown width={16} height={16} />
        </Pressable>
      </View>

      <View style={styles.card}>
        <View style={styles.sectionHeading}>
          <View style={styles.iconTile}><FactCheckIcon customColor="#5423B5" width={18} height={18} /></View>
          <Text style={styles.sectionTitle}>Pencapaian Karakter</Text>
          <Pressable onPress={() => setCharactersOpen(true)} style={styles.seeAllButton}>
            <Text style={styles.seeAll}>Lihat Semua</Text>
          </Pressable>
        </View>
        <View style={styles.progressTrack}>
          <View style={[styles.progressValue, { width: `${progress}%` }]} />
        </View>
        {hasThresholdData ? (
          <View style={styles.progressLabels}>
            <Text style={styles.bodyText}><Text style={styles.bold}>{progress}%</Text> Tercapai</Text>
            <Text style={styles.bodyText}>{failedCount} Perlu Perhatian</Text>
          </View>
        ) : (
          <ReportEmptyState
            compact
            title="Belum ada data pencapaian"
            description="Data karakter dan indikator belum tersedia untuk periode ini."
          />
        )}
        <View style={styles.statsDivider} />
        <View style={styles.statColumns}>
          <View style={styles.statColumn}>
            <View style={styles.statLabelRow}>
              <View style={styles.smallIconTile}><Heart2Icon customColor="#151515" /></View>
              <Text style={styles.statLabel}>Karakter</Text>
            </View>
            <Text style={styles.statValue}>{characterThreshold.passed || 0} dari {(characterThreshold.passed || 0) + (characterThreshold.failed || 0)}</Text>
            <Text style={styles.achieved}>{characterThreshold.passed || characterThreshold.failed ? "Sudah Tercapai" : "Belum ada data"}</Text>
          </View>
          <View style={styles.statColumn}>
            <View style={styles.statLabelRow}>
              <View style={styles.smallIconTile}><ExtensionIcon width={18} height={18} /></View>
              <Text style={styles.statLabel}>Indikator</Text>
            </View>
            <Text style={styles.statValue}>{indicatorThreshold.passed || 0} dari {(indicatorThreshold.passed || 0) + (indicatorThreshold.failed || 0)}</Text>
            <Text style={styles.achieved}>{indicatorThreshold.passed || indicatorThreshold.failed ? "Sudah Tercapai" : "Belum ada data"}</Text>
          </View>
        </View>
      </View>

      <ReportGoodNewsCarousel indicatorData={indicatorData}/>

      <View style={styles.card}>
        <View style={styles.sectionHeading}>
          <View style={styles.iconTile}><Heart2Icon customColor="#5423B5" /></View>
          <Text style={styles.sectionTitle}>TOP 3 Karakter Terbaik</Text>
          <Pressable onPress={() => setCharactersOpen(true)} style={styles.seeAllButton}>
            <Text style={styles.seeAll}>Lihat Semua</Text>
          </Pressable>
        </View>
        <View style={styles.listDivider} />
        {!loading && !loadError && characterData.length === 0 && (
          <ReportEmptyState
            title="Belum ada data karakter"
            description="Karakter belum memiliki indikator pada periode ini."
          />
        )}
        {characterData.map((item) => (
          <ReportCharacterRow key={item.name} item={item} onPress={() => openCharacter(item.name)} />
        ))}
      </View>

      <View style={styles.card}>
        <View style={styles.sectionHeading}>
          <View style={styles.iconTile}><ExtensionIcon customColor="#5423B5" width={18} height={18} /></View>
          <Text style={styles.sectionTitle}>TOP 3 Indikator</Text>
          <Pressable onPress={openAllIndicators} style={styles.seeAllButton}>
            <Text style={styles.seeAll}>Lihat Semua</Text>
          </Pressable>
        </View>
        <View style={styles.segmentRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ selected: indicatorType === "positive" }}
            onPress={() => setIndicatorType("positive")}
            style={[styles.segment, indicatorType === "positive" && styles.segmentPositiveActive]}
          >
            <ThumbUpIcon width={22} height={22} customColor={indicatorType === "positive" ? "#48B920" : "#151515"} />
            <Text style={[styles.segmentText, indicatorType === "positive" && styles.positiveText]}>Perilaku{"\n"}Terbaik</Text>
            {indicatorType === "positive" && <View style={[styles.segmentUnderline, styles.positiveUnderline]} />}
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ selected: indicatorType === "attention" }}
            onPress={() => setIndicatorType("attention")}
            style={[styles.segment, indicatorType === "attention" && styles.segmentAttentionActive]}
          >
            <DangerIcon width={22} height={22} fill={indicatorType === "attention" ? "#D84343" : "#151515"} />
            <Text style={[styles.segmentText, indicatorType === "attention" && styles.attentionText]}>Perlu{"\n"}Perhatian</Text>
            {indicatorType === "attention" && <View style={[styles.segmentUnderline, styles.attentionUnderline]} />}
          </Pressable>
        </View>
        {loading && <ReportEmptyState title="Memuat indikator" description="Data indikator sedang dimuat." />}
        {loadError && <ReportEmptyState title="Data indikator tidak tersedia" description="Coba muat ulang laporan nanti." />}
        {!loading && !loadError && indicatorData[indicatorType].length === 0 && (
          <ReportEmptyState
            title="Belum ada indikator"
            description={`Belum ada indikator ${indicatorType === "positive" ? "terbaik" : "perlu perhatian"} untuk periode ini.`}
          />
        )}

        {indicatorData[indicatorType].map((item, index) => {
          if (indicatorType === "positive" && item.value && item.value >=3 ) {
            return (<ReportIndicatorItem
              key={`${item.name}-${index}`}
              item={item}
              characterName={item.characterName}
              compact
              isLast={index === indicatorData[indicatorType].length - 1}
            />)
          }

          if (indicatorType === "attention" && item.value && item.value <=2 ) {
            return (<ReportIndicatorItem
              key={`${item.name}-${index}`}
              item={item}
              characterName={item.characterName}
              compact
              isLast={index === indicatorData[indicatorType].length - 1}
            />)
          }
        })}
      </View>
    </ScrollView>
    <ReportModalSheet visible={periodOpen} onClose={() => setPeriodOpen(false)}>
      <View style={styles.sheetHeading}>
        <View style={styles.sheetIcon}><CalendarTodayIcon customColor="#6724E2" /></View>
        <Text style={styles.sheetTitle}>Periode</Text>
      </View>
      <View style={styles.sheetDivider} />
      {periods.length === 0 ? (
        <ReportEmptyState
          title="Belum ada periode"
          description="Periode laporan belum tersedia saat ini."
        />
      ) : periods.map((month) => (
        <Pressable
          key={month}
          onPress={() => {
            setPeriod(month);
            setPeriodOpen(false);
          }}
          style={[styles.periodChoice, period === month && styles.periodChoiceSelected]}
        >
          <Text style={[styles.periodChoiceText, period === month && styles.periodChoiceTextSelected]}>{formatPeriod(month)}</Text>
          {period === month && <View style={styles.selectedCheck}><Text style={styles.selectedCheckText}>✓</Text></View>}
        </Pressable>
      ))}
      <Pressable onPress={() => setPeriodOpen(false)} style={styles.closeButton}>
        <Text style={styles.closeButtonText}>Tutup</Text>
      </Pressable>
    </ReportModalSheet>
    <ReportModalSheet visible={childPickerOpen} onClose={() => setChildPickerOpen(false)}>
      <View style={styles.sheetHeading}>
        <View style={styles.sheetIcon}><PersonSearchIcon width={18} height={18} customColor="#5423B5" /></View>
        <Text style={styles.sheetTitle}>Pilih Data Anak</Text>
      </View>
      <View style={styles.sheetDivider} />
      {selectedChild ? (
        <Pressable
          accessibilityRole="radio"
          accessibilityState={{ selected: true }}
          onPress={() => setChildPickerOpen(false)}
          style={styles.childChoice}
        >
          <View style={styles.childAvatar}><ProfileIcon width={18} height={18} /></View>
          <View style={styles.childDetails}>
            <Text style={styles.childName}>{formatCharacterName(selectedChild)}</Text>
            <Text style={styles.childClass}>Kelas {formatCharacterName(selectedProfile)}</Text>
          </View>
          <View style={styles.selectedCheck}><Text style={styles.selectedCheckText}>✓</Text></View>
        </Pressable>
      ) : (
        <ReportEmptyState
          title="Belum ada data anak"
          description="Data anak belum tersedia untuk laporan ini."
        />
      )}
      <Pressable onPress={() => setChildPickerOpen(false)} style={styles.closeButton}>
        <Text style={styles.closeButtonText}>Tutup</Text>
      </Pressable>
    </ReportModalSheet>
    <ReportModalSheet visible={charactersOpen} onClose={() => setCharactersOpen(false)} contentStyle={styles.characterSheet}>
      <View style={styles.sheetHeading}>
        <View style={styles.sheetIcon}><Heart2Icon customColor="#5423B5" /></View>
        <Text style={styles.sheetTitle}>Pencapaian Karakter</Text>
      </View>
      <View style={styles.sheetDivider} />
      <ScrollView style={styles.characterSheetList} showsVerticalScrollIndicator={false}>
        {loading || loadError || charactersWithData.length === 0 ? (
          <ReportEmptyState
            title={loading ? "Memuat karakter" : loadError ? "Data karakter tidak tersedia" : "Belum ada data karakter"}
            description={loading ? "Data karakter sedang dimuat." : loadError ? "Coba muat ulang laporan nanti." : "Karakter belum memiliki indikator pada periode ini."}
          />
        ) : charactersWithData.map((item) => (
          <ReportCharacterRow
            key={item.name}
            item={item}
            onPress={() => {
              setCharactersOpen(false);
              openCharacter(item.name);
            }}
          />
        ))}
      </ScrollView>
      <Pressable onPress={() => setCharactersOpen(false)} style={styles.closeButton}>
        <Text style={styles.closeButtonText}>Tutup</Text>
      </Pressable>
    </ReportModalSheet>
    </ReportBackground>
  );
}

const styles = StyleSheet.create({
  loadingScreen: { flex: 1, alignItems: "center", justifyContent: "center" },
  loadingContent: { alignItems: "center", gap: 12 },
  loadingText: { color: "#5423B5", fontFamily: "Archivo-Regular", fontSize: 12 },
  screen: { flex: 1, backgroundColor: "transparent" },
  content: { padding: 16, paddingTop: 28, paddingBottom: 32, gap: 16 },
  apiMessage: { color: "#666666", fontFamily: "Archivo-Regular", fontSize: 11 },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 10 },
  avatar: { width: 42, height: 42, borderRadius: 21, backgroundColor: "#FFFFFF", alignItems: "center", justifyContent: "center" },
  inputData: { height: 42, paddingHorizontal: 14, borderRadius: 24, backgroundColor: "#FFFFFF", flexDirection: "row", alignItems: "center", gap: 8 },
  inputDataText: { color: "#151515", fontFamily: "Archivo-Bold", fontSize: 12 },
  greeting: { paddingTop: 12, paddingBottom: 10 },
  greetingTitle: { color: "#111111", fontFamily: "Archivo-Bold", fontSize: 21 },
  greetingSubtitle: { color: "#171717", fontFamily: "Archivo-Regular", fontSize: 20, marginTop: 2 },
  schoolPeriodCard: { paddingHorizontal: 16, borderRadius: 17, backgroundColor: "#FFFFFF", elevation: 2, shadowColor: "#34234A", shadowOpacity: 0.08, shadowRadius: 5, shadowOffset: { width: 0, height: 2 } },
  schoolInfo: { minHeight: 60, flexDirection: "row", alignItems: "center", gap: 12 },
  schoolLogo: { width: 68, height: 20 },
  schoolDivider: { width: 1, height: 28, backgroundColor: "#E7E7E7" },
  schoolName: { flex: 1, color: "#353535", fontFamily: "Archivo-Regular", fontSize: 11 },
  schoolPeriodDivider: { height: 1, backgroundColor: "#E7E7E7" },
  periodButton: { minHeight: 54, flexDirection: "row", alignItems: "center", gap: 12 },
  periodLabel: { flex: 1, color: "#151515", fontFamily: "Archivo-Bold", fontSize: 12 },
  periodValue: { color: "#5423B5", fontFamily: "Archivo-Regular", fontSize: 12 },
  periodUnavailable: { color: "#999999" },
  seeAllButton: { borderRadius: 16 },
  card: { padding: 14, borderRadius: 17, backgroundColor: "#FFFFFF", elevation: 2, shadowColor: "#34234A", shadowOpacity: 0.08, shadowRadius: 5, shadowOffset: { width: 0, height: 2 } },
  sectionHeading: { minHeight: 28, flexDirection: "row", alignItems: "center", gap: 8 },
  iconTile: { width: 22, height: 22, borderRadius: 5, backgroundColor: "#F0E9FF", alignItems: "center", justifyContent: "center" },
  sectionTitle: { flex: 1, color: "#171717", fontFamily: "Archivo-Bold", fontSize: 12 },
  seeAll: { overflow: "hidden", paddingHorizontal: 10, paddingVertical: 6, borderRadius: 15, color: "#6724E2", backgroundColor: "#F0E9FF", fontFamily: "Archivo-Regular", fontSize: 9 },
  progressTrack: { height: 20, marginTop: 12, borderRadius: 12, backgroundColor: "#F0F0F0", overflow: "hidden" },
  progressValue: { height: "100%", borderRadius: 12, backgroundColor: "#5423B5" },
  progressLabels: { marginTop: 7, flexDirection: "row", justifyContent: "space-between" },
  bodyText: { color: "#202020", fontFamily: "Archivo-Regular", fontSize: 11 },
  bold: { fontFamily: "Archivo-Bold" },
  statsDivider: { height: 1, marginTop: 12, backgroundColor: "#EEEEEE" },
  statColumns: { flexDirection: "row", marginTop: 12 },
  statColumn: { flex: 1, gap: 4 },
  statLabelRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  smallIconTile: { width: 24, height: 24, borderRadius: 5, backgroundColor: "#F6F6F6", alignItems: "center", justifyContent: "center" },
  statLabel: { color: "#171717", fontFamily: "Archivo-Bold", fontSize: 12 },
  statValue: { marginTop: 2, color: "#111111", fontFamily: "Archivo-Bold", fontSize: 17 },
  achieved: { color: "#48B920", fontFamily: "Archivo-Regular", fontSize: 12 },
  listDivider: { height: 1, marginTop: 10, marginBottom: 6, backgroundColor: "#EEEEEE" },
  segmentRow: { flexDirection: "row", gap: 10, marginTop: 12, marginBottom: 4 },
  segment: { position: "relative", flex: 1, minHeight: 50, paddingHorizontal: 10, borderWidth: 1, borderColor: "#E7E7E7", borderRadius: 9, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8 },
  segmentPositiveActive: { borderColor: "#48B920", backgroundColor: "#FFFFFF" },
  segmentAttentionActive: { borderColor: "#E24B4B", backgroundColor: "#FFFFFF" },
  segmentUnderline: { position: "absolute", left: 16, right: 16, bottom: 2, height: 4, borderRadius: 3 },
  positiveUnderline: { backgroundColor: "#48B920" },
  attentionUnderline: { backgroundColor: "#E24B4B" },
  segmentText: { color: "#252525", fontFamily: "Archivo-Regular", fontSize: 11, lineHeight: 14 },
  positiveText: { color: "#48B920" },
  attentionText: { color: "#D84343" },
  characterSheet: { height: "76%" },
  sheetHeading: { minHeight: 26, flexDirection: "row", alignItems: "center", gap: 8 },
  sheetIcon: { width: 22, height: 22, borderRadius: 5, backgroundColor: "#F0E9FF", alignItems: "center", justifyContent: "center" },
  sheetTitle: { color: "#171717", fontFamily: "Archivo-Bold", fontSize: 12 },
  sheetDivider: { height: 1, marginTop: 12, marginBottom: 14, backgroundColor: "#EEEEEE" },
  periodChoice: { minHeight: 48, marginBottom: 12, paddingHorizontal: 14, borderWidth: 1, borderColor: "#E8E8E8", borderRadius: 14, flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  periodChoiceSelected: { borderColor: "#6724E2" },
  periodChoiceText: { color: "#222222", fontFamily: "Archivo-Regular", fontSize: 12 },
  periodChoiceTextSelected: { color: "#6724E2", fontFamily: "Archivo-Bold" },
  childChoice: { minHeight: 62, paddingHorizontal: 12, borderWidth: 1, borderColor: "#6724E2", borderRadius: 14, flexDirection: "row", alignItems: "center", gap: 12 },
  childAvatar: { width: 32, height: 32, borderRadius: 16, backgroundColor: "#EEEEEE", alignItems: "center", justifyContent: "center" },
  childDetails: { flex: 1 },
  childName: { color: "#5423B5", fontFamily: "Archivo-Bold", fontSize: 12 },
  childClass: { marginTop: 2, color: "#999999", fontFamily: "Archivo-Regular", fontSize: 11 },
  selectedCheck: { width: 18, height: 18, borderRadius: 9, backgroundColor: "#48B920", alignItems: "center", justifyContent: "center" },
  selectedCheckText: { color: "#FFFFFF", fontFamily: "Archivo-Bold", fontSize: 12, lineHeight: 16 },
  characterSheetList: { flex: 1 },
  closeButton: { minHeight: 42, marginTop: 8, borderRadius: 22, backgroundColor: "#F0E9FF", alignItems: "center", justifyContent: "center" },
  closeButtonText: { color: "#6724E2", fontFamily: "Archivo-Bold", fontSize: 11 },
});